import { useCallback, useEffect, useState } from 'react';
import { registerFCMToken } from '../api/notifications.api';
import {
  getCurrentToken,
  getNotificationPermission,
  isNotificationSupported,
  onForegroundMessage,
  requestNotificationPermission
} from '../config/firebase';
import type {
  FCMActions,
  FCMContextState,
  FirebaseMessagePayload,
  NotificationPermissionState
} from '../types/fcm.types';
import { getDeviceInfo } from '../utils/deviceInfo';
import { globalNotificationToast } from '../utils/globalNotificationToast';

interface UseFirebaseNotificationsProps {
  jwtToken: string | null;
  onNotificationReceived?: (payload: FirebaseMessagePayload) => void;
}

export const useFirebaseNotifications = ({
  jwtToken,
  onNotificationReceived
}: UseFirebaseNotificationsProps): FCMContextState & FCMActions => {
  const [fcmToken, setFcmToken] = useState<string | null>(
    localStorage.getItem('fcm_token')
  );
  const [permissionState, setPermissionState] =
    useState<NotificationPermissionState>({
      permission: getNotificationPermission(),
      supported: isNotificationSupported(),
      requested: false
    });
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Update permission state
   */
  const updatePermissionState = useCallback(() => {
    setPermissionState((prev) => ({
      ...prev,
      permission: getNotificationPermission()
    }));
  }, []);

  /**
   * Request notification permission
   */
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!permissionState.supported) {
      setError('Notifications not supported on this device');
      return false;
    }

    try {
      setPermissionState((prev) => ({ ...prev, requested: true }));

      const token = await requestNotificationPermission();

      updatePermissionState();

      if (token) {
        setFcmToken(token);
        localStorage.setItem('fcm_token', token);
        setError(null);
        return true;
      } else {
        setError('Failed to get notification permission');
        return false;
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Permission request failed';
      setError(errorMessage);
      console.error('Permission request error:', err);
      return false;
    }
  }, [permissionState.supported, updatePermissionState]);

  /**
   * Register FCM token with backend
   */
  const registerTokenWithBackend = useCallback(
    async (token: string): Promise<boolean> => {
      if (!jwtToken) {
        return false;
      }

      try {
        const deviceInfo = getDeviceInfo();
        const payload = {
          fcm_token: token,
          ...deviceInfo
        };

        const response = await registerFCMToken(payload);

        if (response.success) {
          return true;
        } else {
          throw new Error(response.message || 'Registration failed');
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Token registration failed';
        setError(errorMessage);
        console.error('FCM token registration error:', err);
        return false;
      }
    },
    [jwtToken]
  );

  /**
   * Initialize FCM (called after successful login)
   */
  const initializeFCM = useCallback(async (): Promise<boolean> => {
    if (!permissionState.supported) {
      return false;
    }

    if (!jwtToken) {
      return false;
    }

    setIsInitializing(true);
    setError(null);

    try {
      // Check if we already have permission
      if (permissionState.permission === 'granted') {
        // Get existing or current token
        let token = fcmToken;
        if (!token) {
          token = await getCurrentToken();
          if (token) {
            setFcmToken(token);
            localStorage.setItem('fcm_token', token);
          }
        }

        if (token) {
          const registered = await registerTokenWithBackend(token);
          if (registered) {
            setIsInitializing(false);
            return true;
          }
        } else {
          console.warn('No FCM token available for registration');
        }
      }

      const permissionGranted = await requestPermission();

      if (permissionGranted && fcmToken) {
        const registered = await registerTokenWithBackend(fcmToken);
        setIsInitializing(false);
        return registered;
      }

      setIsInitializing(false);
      return false;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'FCM initialization failed';
      setError(errorMessage);
      console.error('FCM initialization error:', err);
      setIsInitializing(false);
      return false;
    }
  }, [
    permissionState.supported,
    permissionState.permission,
    fcmToken,
    jwtToken,
    requestPermission,
    registerTokenWithBackend
  ]);

  /**
   * Refresh FCM token - simplified to only get current token
   */
  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (!jwtToken) {
      console.warn('No JWT token available for token refresh');
      return false;
    }

    try {
      const currentToken = await getCurrentToken();
      if (currentToken) {
        setFcmToken(currentToken);
        localStorage.setItem('fcm_token', currentToken);
        return true;
      }
      return false;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Token refresh failed';
      setError(errorMessage);
      console.error('FCM token refresh error:', err);
      return false;
    }
  }, [jwtToken]);

  /**
   * Clear FCM token (called on logout) - only clears local state
   */
  const clearFCM = useCallback(async (): Promise<void> => {
    // Clear local state only
    setFcmToken(null);
    localStorage.removeItem('fcm_token');
    setError(null);
  }, []);

  /**
   * Handle foreground messages
   */
  useEffect(() => {
    if (!fcmToken || permissionState.permission !== 'granted') {
      return;
    }

    const unsubscribe = onForegroundMessage(
      (payload: FirebaseMessagePayload) => {
        // Show custom toast notification for foreground messages
        if (payload.notification) {
          if (globalNotificationToast) {
            globalNotificationToast.showNotification({
              title: payload.notification.title,
              body: payload.notification.body,
              icon: payload.notification.icon
            });
          }
        }

        // Call custom handler if provided
        if (onNotificationReceived) {
          onNotificationReceived(payload);
        }
      }
    );

    return unsubscribe;
  }, [fcmToken, permissionState.permission, onNotificationReceived]);

  /**
   * Auto-initialize FCM when JWT token becomes available
   */
  useEffect(() => {
    if (jwtToken && !fcmToken && permissionState.supported && !isInitializing) {
      initializeFCM().then((success) => {
        if (success) {
          console.log('FCM auto-initialization successful');
        }
      });
    }
  }, [
    jwtToken,
    fcmToken,
    permissionState.supported,
    permissionState.permission,
    isInitializing,
    initializeFCM
  ]);

  /**
   * Update permission state when it changes
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updatePermissionState();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [updatePermissionState]);

  return {
    // State
    fcmToken,
    permissionState,
    isInitializing,
    error,
    // Actions
    requestPermission,
    initializeFCM,
    clearFCM,
    refreshToken
  };
};
